import React, { useEffect, useState } from "react";
import {
  useMediaQuery,
  useTheme,
  Box,
  Skeleton,
  Typography,
} from "@mui/material";
import {
  BarChart,
  LineChart,
  PieChart,
  Bar,
  Line,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from "recharts";
import AuthService from "../../../../../AuthService.js";
import utils from "../../../../../utils/index.js";
import Card from "../../Card/Card.js";
import {
  receitaAcumulada,
  numVendas,
  receitaPorDiaDaSemana,
  receitaTipoPedido,
  valorItensMaisVendidos,
  quantItensMaisVendidos,
} from "./MockData";
import { useSnackbar } from "notistack";

const COLORS = ["#2F3559", "#F2C26A", "#8884d8", "#82ca9d", "#ffc658"];

const formatTooltip = (value, name) => {
  if (["Quantidade vendida", "Quantidade"].includes(name)) return [value, name];
  if (
    [
      "Valor vendido",
      "Mês anterior",
      "Mês atual",
      "Total de venda do produto",
      "Valor",
    ].includes(name)
  )
    return [utils.formatNumber(parseFloat(value), "decimal"), name];
  if (name === "Porcentagem")
    return [utils.formatNumber(parseFloat(value).toFixed(2)), name];
  return [value, name];
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <Box
        className="custom-tooltip"
        style={{
          backgroundColor: "#fff",
          padding: 10,
          borderRadius: "0.5rem",
          border: "1px solid #ccc",
        }}
      >
        <p className="label">{label}</p>
        {payload.map((entry, i) => {
          const [val, name] = formatTooltip(entry.value, entry.name);
          return (
            <p key={i} style={{ color: entry.color }}>{`${name}: ${val}`}</p>
          );
        })}
      </Box>
    );
  }
  return null;
};

const renderLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos((-midAngle * Math.PI) / 180);
  const y = cy + radius * Math.sin((-midAngle * Math.PI) / 180);
  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const CustomizedAxisTick = ({ x, y, payload }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const fontLength = isMobile ? 4 : isTablet ? 8 : 12;
  const truncatedValue =
    payload.value.length > fontLength
      ? `${payload.value.slice(0, fontLength)}...`
      : payload.value;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={0}
        textAnchor="end"
        fill="#666"
        style={{ fontSize: "14px" }}
      >
        {truncatedValue}
      </text>
    </g>
  );
};

const formatYAxis = (tick) => utils.formatNumber(tick, "decimal");

export default function CardGenerico({ url, title, nameChart }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMock = false;
  useEffect(() => {
    setLoading(true);
    if (isMock) {
      const mockMap = {
        receitaAcumulada,
        numVendas,
        receitaPorDiaDaSemana,
        receitaTipoPedido,
        valorItensMaisVendidos,
        quantItensMaisVendidos,
      };

      setData(mockMap[nameChart] || null);
      setLoading(false);
    } else {
      new AuthService()
        .get(url)
        .then(setData)
        .catch((err) => {
          console.error(err);
          enqueueSnackbar("Erro ao carregar os dados do gráfico.", {
            variant: "error",
          });
          setData(null);
        })
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, nameChart]);

  const chartData = React.useMemo(() => {
    if (!data || !data.series || !data.series[0]) return [];

    const { valueMesAnterior, valueMesAtual } = data.series[0];
    return data.labels.map((label, i) => ({
      labels: label,
      valueMesAnterior: valueMesAnterior ? valueMesAnterior[i] : null,
      valueMesAtual: valueMesAtual ? valueMesAtual[i] : null,
      pieChartValues: valueMesAtual ? valueMesAtual[i] : null,
    }));
  }, [data]);

  const renderChart = () => {
    const outerRadius = isMobile ? 100 : 150;

    switch (nameChart) {
      case "numVendas":
        return (
          <ComposedChart data={chartData}>
            <XAxis dataKey="labels" />
            <YAxis tickFormatter={formatYAxis} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="valueMesAtual" name="Valor vendido" fill="#2F3559" />
            <Line
              dataKey="valueMesAnterior"
              name="Quantidade"
              stroke="#F2C26A"
            />
          </ComposedChart>
        );
      case "receitaAcumulada":
        return (
          <LineChart data={chartData}>
            <XAxis dataKey="labels" />
            <YAxis tickFormatter={formatYAxis} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line
              dataKey="valueMesAnterior"
              name="Mês anterior"
              stroke="#2F3559"
              strokeWidth={2}
              dot={{ r: 3 }}
            />
            <Line
              dataKey="valueMesAtual"
              name="Mês atual"
              stroke="#F2C26A"
              strokeWidth={2}
              dot={{ r: 3 }}
            />
          </LineChart>
        );
      case "receitaTipoPedido":
        return (
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderLabel}
              outerRadius={outerRadius}
              fill="#8884d8"
              dataKey="pieChartValues"
              nameKey="labels"
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend />
          </PieChart>
        );
      case "receitaPorDiaDaSemana":
        const diasSemana = {
          0: "Dom",
          1: "Seg",
          2: "Ter",
          3: "Qua",
          4: "Qui",
          5: "Sex",
          6: "Sáb",
        };

        const chartDataFormatado = chartData.map((item) => ({
          ...item,
          labels: diasSemana[item.labels] || item.labels,
        }));
        return (
          <ComposedChart data={chartDataFormatado}>
            <XAxis dataKey="labels" />
            <YAxis tickFormatter={formatYAxis} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="valueMesAtual" name="Valor" fill="#2F3559" />
            <Line
              dataKey="valueMesAnterior"
              name="Quantidade"
              stroke="#F2C26A"
            />
          </ComposedChart>
        );
      case "valorItensMaisVendidos":
      case "quantItensMaisVendidos":
        return (
          <BarChart data={chartData.reverse()} layout="vertical">
            <XAxis
              type="number"
              tickFormatter={
                nameChart === "valorItensMaisVendidos" ? formatYAxis : undefined
              }
            />
            <YAxis
              dataKey="labels"
              type="category"
              width={120}
              interval={0}
              tick={<CustomizedAxisTick />}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar
              dataKey="valueMesAtual"
              name={
                nameChart === "valorItensMaisVendidos"
                  ? "Valor vendido"
                  : "Quantidade vendida"
              }
              fill={
                nameChart === "valorItensMaisVendidos" ? "#2F3559" : "#F2C26A"
              }
            />
          </BarChart>
        );
      default:
        return null;
    }
  };
  return (
    <Card>
      <Box padding="3rem">
        {loading ? (
          <Skeleton
            variant="rectangular"
            width="100%"
            height={450}
            animation="wave"
            sx={{ borderRadius: 2 }}
          />
        ) : !!data && chartData.length > 0 ? (
          <>
            <Typography variant={isMobile ? "subtitle1" : "h6"}>
              {title}
            </Typography>
            <Box style={{ width: "100%", height: isMobile ? 220 : 400 }}>
              <Box
                marginTop="2rem"
                style={{ width: "100%", height: isMobile ? 250 : 400 }}
              >
                <ResponsiveContainer>{renderChart()}</ResponsiveContainer>
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Typography variant={isMobile ? "subtitle1" : "h6"}>
              {title}
            </Typography>
            <Box style={{ width: "100%", height: isMobile ? 220 : 400 }}>
              <Box
                marginTop="2rem"
                display="flex"
                justifyContent="center"
                alignItems="center"
                style={{ width: "100%", height: isMobile ? 250 : 400 }}
              >
                <Typography variant="subtitle1" textAlign="center">
                  Nenhum dado encontrado
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Card>
  );
}
